import { useId } from 'react';

interface DownreadLogoProps {
  size?: number;
  showText?: boolean;
}

const DownreadLogo: React.FC<DownreadLogoProps> = ({ size = 32, showText = false }) => {
  const id = useId().replace(/:/g, '');
  const vw = showText ? 900 : 200;
  const vh = 200;
  const width = showText ? size * (vw / vh) : size;
  const markPath = 'M30 20h70c44 0 80 36 80 80s-36 80-80 80H30V20z';

  return (
    <svg
      width={width}
      height={size}
      viewBox={`0 0 ${vw} ${vh}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={`${id}-base`} x1="0.05" y1="0" x2="0.95" y2="1">
          <stop offset="0%" stopColor="#5F90EE" />
          <stop offset="45%" stopColor="#4C86F3" />
          <stop offset="100%" stopColor="#285ED0" />
        </linearGradient>

        <linearGradient id={`${id}-glass`} x1="0.25" y1="0.05" x2="0.6" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.34" />
          <stop offset="34%" stopColor="#FFFFFF" stopOpacity="0.09" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        <radialGradient id={`${id}-shine`} cx="0.3" cy="0.15" r="0.8">
          <stop offset="0%" stopColor="#DCEAFF" stopOpacity="0.34" />
          <stop offset="50%" stopColor="#DCEAFF" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#DCEAFF" stopOpacity="0" />
        </radialGradient>

        <linearGradient id={`${id}-border`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.15" />
        </linearGradient>

        <filter id={`${id}-markShadow`} x="-12%" y="-12%" width="130%" height="130%">
          <feDropShadow dx="0" dy="6" stdDeviation="7.5" floodColor="#5A8EEB" floodOpacity="0.16" />
        </filter>

        <filter id={`${id}-arrowShadow`} x="-20%" y="-20%" width="160%" height="180%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="2.2" floodColor="#14397E" floodOpacity="0.35" />
        </filter>

        <clipPath id={`${id}-clip`}>
          <path d={markPath} />
        </clipPath>

        {showText && (
          <>
            <linearGradient id={`${id}-textGrad`} x1="0" y1="0.1" x2="0.8" y2="1">
              <stop offset="0%" stopColor="#4B84F0" />
              <stop offset="100%" stopColor="#1F4EB3" />
            </linearGradient>

            <filter id={`${id}-textShadow`} x="-5%" y="-5%" width="112%" height="120%">
              <feDropShadow dx="0" dy="1" stdDeviation="1.6" floodColor="#5A8EEB" floodOpacity="0.14" />
            </filter>
          </>
        )}
      </defs>

      <g filter={`url(#${id}-markShadow)`}>
        <path d={markPath} fill={`url(#${id}-base)`} />
        <path d={markPath} fill={`url(#${id}-shine)`} />
        <path d={markPath} fill={`url(#${id}-glass)`} clipPath={`url(#${id}-clip)`} />
        <path d={markPath} fill="none" stroke={`url(#${id}-border)`} strokeWidth="1.4" />
      </g>

      <g filter={`url(#${id}-arrowShadow)`}>
        <path
          d="M100 55v85M68 105l32 35 32-35"
          stroke="white"
          strokeWidth="12.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity="0.96"
        />
      </g>

      {showText && (
        <text
          x="186"
          y="175"
          fill={`url(#${id}-textGrad)`}
          filter={`url(#${id}-textShadow)`}
          fontFamily="'Inter Variable', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif"
          fontSize="165"
          fontWeight="630"
          letterSpacing="-2.8"
        >
          ownread
        </text>
      )}
    </svg>
  );
};

export default DownreadLogo;
